// src/pages/DifferentialSilhouette/index.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components
import BreadCrumb from "../../Components/Common/BreadCrumb";
import GeneralSettings from "../../Components/Common/GeneralSettings";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";
import PlotContainer from "../../Components/Common/PlotContainer";

// Page-specific Components
import GroupManager from "./components/GroupManager";

// API
import {
  getDifferentialSilhouetteData,
  generateDifferentialSilhouettePlot,
} from "../../helpers/eirna_backend_helper";

// Hooks
import { useSettings } from "../../hooks/useSettings";
import { useFileSelection } from "../../hooks/useFileSelection";
import { useSmartReadLength } from "../../hooks/useSmartReadLength";
import { useGroupsManager } from "../../hooks/useGroupsManager";
import { useJobPolling } from "../../hooks/useJobPolling";
import { EnterButtonListener } from "../../hooks/EnterButtonListener";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Short code handling
import {
  useDifferentialSilhouetteShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";

// Config
import {
  differentialSilhouetteConfig,
  differentialSilhouetteDefaults,
  differentialSilhouetteValidation,
} from "../../config/plotConfigs/differentialSilhouetteConfig";
import ActionButton from "../../Components/Common/ActionButton";

const DifferentialSilhouette = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [loading, setLoading] = useState(true);
  const [plotData, setPlotData] = useState(null);
  const [error, setError] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  // References to plotContainer and loadingDiv, to allow for auto scrolling
  const loadingDivRef  = useRef(null);
  const plotContainerRef  = useRef(null);

  const [apiData, setApiData] = useState({
    json_files: [],
    organism: "",
    transcriptome: "",
  });

  // Hooks
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useSettings(
    differentialSilhouetteDefaults,
    differentialSilhouetteValidation
  );

  const {
    selectedFiles,
    selectedSeqType,
    selectedGroup,
    toggleFile,
    toggleAllFiles,
    setSelectedSeqType,
    setSelectedGroup,
    clearSelection,
    selectMultipleFiles,
    deselectMultipleFiles,
    setSelectedFiles,
  } = useFileSelection("riboseq");

  const {
    getStandardReadLengths,
    markFieldAsUserModified,
    resetUserModifications,
    shouldAutoAdjust,
  } = useSmartReadLength();

  // Use the shared groups manager hook - SAME AS PAUSEDETECTION
  const {
    groups,
    addFilesToGroup,
    removeFilesFromGroup,
    updateGroupName,
    clearGroup,
    clearAllGroups,
    handleAutoFill,
    handleAutoLabel,
    resetGroups,
  } = useGroupsManager([
    { id: "group1", name: "Group 1", files: [] }, // Uses 'files' property
    { id: "group2", name: "Group 2", files: [] }, // Uses 'files' property
  ]);

  const { plotLoading, taskId, startJob } = useJobPolling();

  // Use the Differential Silhouette specific short code handler
  const { autoRunPlot, 
          setAutoRunPlot, 
          consumeUserPassedShortCode } =
    useDifferentialSilhouetteShortCode({
      location,
      updateMultipleSettings,
      resetGroups,
    });

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (plotLoading) return;
    setAutoRunPlot(true);
  }, [plotLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

  // Scroll to loadingDiv anytime plotLoading changes
  useScrollToRef(loadingDivRef, plotLoading);

  // Scroll to plot/table anytime plotData changes
  useScrollToRef(plotContainerRef, plotData);

  // Load data on component mount
  useEffect(() => {
    if (!projectId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const response = await getDifferentialSilhouetteData(projectId);
        const data = response?.data || response || {};

        setApiData({
          json_files: data.json_files || [],
          organism: data.organism || "",
          transcriptome: data.transcriptome || "",
        });

        // Set default seq type
        const fileTypes = data.json_files
          ? [...new Set(data.json_files.map((x) => x.file_type))]
          : [];
        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        // Set default group
        const uniqueGroups = [
          ...new Set(
            data.json_files?.map((file) => file.subgroup).filter(Boolean) || []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }

        toast.success("Project data loaded successfully");
      } catch (err) {
        console.error("Failed to load project data:", err);
        const errorMsg = "Failed to load project data";
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, setSelectedSeqType, setSelectedGroup]);

  // Auto-trigger analysis generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        plotLoading,
        loading,
        groupsWithFiles: groups.filter((g) => g.files.length > 0).length,
      });

      const hasFilesForComparison =
        groups.length >= 2 && groups.every((g) => g.files.length > 0);

      if (!plotLoading && !loading && hasFilesForComparison) {
        console.log(
          "All conditions met, generating differential silhouette analysis from short code..."
        );
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGenerate();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [
    autoRunPlot,
    plotLoading,
    loading,
    groups.map((g) => g.files.length).join(","),
    setAutoRunPlot,
  ]);

  useEffect(() => {
    // Helper to extract file IDs from a group (supports group.files as objects or IDs, and group.fileIds)
    const extractIdsFromGroup = (g) => {
      if (!g) return [];
      if (Array.isArray(g.files) && g.files.length > 0) {
        // files may be objects or primitive ids
        const first = g.files[0];
        if (typeof first === "object" && first !== null) {
          return g.files.map((f) => f.file_id ?? f.id ?? f); // prefer file_id, fallback to id or value
        }
        return g.files;
      }
      if (Array.isArray(g.fileIds)) {
        return g.fileIds;
      }
      return [];
    };

    // Collect all file IDs from all groups and dedupe
    const fileIdsForAdjustment = Array.from(
      new Set(groups.flatMap((g) => extractIdsFromGroup(g)).filter(Boolean))
    );

    // Need project file metadata and at least one file to adjust
    if (!apiData.json_files?.length || fileIdsForAdjustment.length === 0 || location.state?.isFromShortCode)
      return;

    // Only auto-adjust if both min and max are allowed to be auto-adjusted
    if (
      shouldAutoAdjust(
        "minread",
        settings.minReadLength,
        settings.maxReadLength
      ) &&
      shouldAutoAdjust(
        "maxread",
        settings.minReadLength,
        settings.maxReadLength
      )
    ) {
      const { min, max } = getStandardReadLengths(
        fileIdsForAdjustment,
        apiData.json_files
      );

      if (settings.minReadLength !== min || settings.maxReadLength !== max) {
        updateMultipleSettings({
          minReadLength: min,
          maxReadLength: max,
        });
        toast.info(`Read lengths auto-adjusted: Min=${min}, Max=${max}`, {
          autoClose: 3000,
        });
      }
    }
  }, [
    groups,
    apiData.json_files,
    getStandardReadLengths,
    shouldAutoAdjust,
    updateMultipleSettings,
    settings.minread,
    settings.maxread,
  ]);
  // Computed values
  const filteredFiles = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    let files = apiData.json_files;
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((x) => x.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((x) => x.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  const seqTypes = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const types = [...new Set(apiData.json_files.map((x) => x.file_type))];
    return types.map((type) => ({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      count: apiData.json_files.filter((x) => x.file_type === type).length,
    }));
  }, [apiData.json_files]);

  const availableGroups = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const groupSet = new Set(
      apiData.json_files.map((x) => x.subgroup).filter(Boolean)
    );
    return Array.from(groupSet).map((group) => ({
      value: group,
      label: group,
      count: apiData.json_files.filter((x) => x.subgroup === group).length,
    }));
  }, [apiData.json_files]);

  const allSeqTypeFilesSelected = useMemo(() => {
    if (!selectedSeqType || selectedSeqType === "all") return false;
    const seqTypeFiles = filteredFiles.filter(
      (x) => x.file_type === selectedSeqType
    );
    return (
      seqTypeFiles.length > 0 &&
      seqTypeFiles.every((x) => selectedFiles.includes(x.file_id))
    );
  }, [selectedSeqType, filteredFiles, selectedFiles]);

  const allGroupFilesSelected = useMemo(() => {
    if (!selectedGroup || selectedGroup === "all") return false;
    const groupFiles = filteredFiles.filter(
      (x) => x.subgroup === selectedGroup
    );
    return (
      groupFiles.length > 0 &&
      groupFiles.every((x) => selectedFiles.includes(x.file_id))
    );
  }, [selectedGroup, filteredFiles, selectedFiles]);

  // Event handlers
  const handleSettingUpdate = useCallback(
    (key, value) => {
      updateSetting(key, value);
      // Mark read length fields as user modified when manually changed
      if (key === "minReadLength" || key === "maxReadLength") {
        markFieldAsUserModified(key);
      }
    },
    [updateSetting, markFieldAsUserModified]
  );

  const handleReset = useCallback(() => {
    resetSettings();
    resetUserModifications();
    clearSelection();
    resetGroups([
      { id: "group1", name: "Group 1", files: [] },
      { id: "group2", name: "Group 2", files: [] },
    ]);
    setPlotData(null);
    setError(null);
    toast.info("Settings reset to defaults");
  }, [resetSettings, resetUserModifications, clearSelection, resetGroups]);

  const handleSelectAllSeqTypeFiles = useCallback(
    (checked) => {
      if (!selectedSeqType || selectedSeqType === "all") return;
      const seqTypeFiles = filteredFiles.filter(
        (x) => x.file_type === selectedSeqType
      );
      const fileIds = seqTypeFiles.map((x) => x.file_id);

      if (checked) {
        selectMultipleFiles(fileIds);
      } else {
        deselectMultipleFiles(fileIds);
      }
    },
    [selectedSeqType, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  const handleSelectAllGroupFiles = useCallback(
    (checked) => {
      if (!selectedGroup || selectedGroup === "all") return;
      const groupFiles = filteredFiles.filter(
        (x) => x.subgroup === selectedGroup
      );
      const fileIds = groupFiles.map((x) => x.file_id);

      if (checked) {
        selectMultipleFiles(fileIds);
      } else {
        deselectMultipleFiles(fileIds);
      }
    },
    [selectedGroup, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  // Build master file dict from groups - SAME LOGIC AS PAUSEDETECTION
  const buildMasterFileDict = useCallback(() => {
    const masterDict = {};

    groups.forEach((group) => {
      // Use group.id as the key (e.g., "group1", "group2") — not group.name
      if (group.files && group.files.length > 0) {
        masterDict[group.id] = {
          file_ids: group.files.map(String),
          file_names: group.files
            .map((fileId) => {
              const file = apiData.json_files.find((f) => f.file_id === fileId);
              return file ? file.file_name : "";
            })
            .filter(Boolean),
        };
      }
    });

    return masterDict;
  }, [groups, apiData.json_files]);

  // Build payload for API
  const buildPayload = useCallback(() => {
    const shortCodeForThisRequest = consumeUserPassedShortCode();
    // Use the Differential Silhouette specific payload creator
    return createPlotSpecificPayload.differentialSilhouette({
      settings,
      projectId,
      userPassedShortCode: shortCodeForThisRequest,
      groups,
      buildMasterFileDict,
    });
  }, [settings, groups, projectId, consumeUserPassedShortCode, buildMasterFileDict]);

  // Generate analysis
  const handleGenerate = useCallback(async () => {
    if (plotLoading) {
      console.log("Job already in progress");
      return;
    }

    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    // Validate groups for comparison
    const group1Files = groups[0]?.files?.length || 0;
    const group2Files = groups[1]?.files?.length || 0;

    if (group1Files === 0 || group2Files === 0) {
      toast.error("Please assign files to both groups for comparison");
      return;
    }

    if (!groups[0]?.name?.trim() || !groups[1]?.name?.trim()) {
      toast.error("Please provide names for both groups");
      return;
    }

    const payload = buildPayload();
    console.log("Submitting differential silhouette payload:", payload);

    const onSuccess = (result) => {
      setPlotData(result.plot || result);
      setError(null);
    };

    const onError = (errorMsg) => {
      setError(errorMsg);
      setPlotData(null);
    };

    startJob(generateDifferentialSilhouettePlot, payload, onSuccess, onError);
  }, [plotLoading, validateSettings, groups, buildPayload, startJob]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#2F9E37" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "Differential Silhouette Analysis | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Differential Silhouette Analysis" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  Differential Silhouette Analysis
                </h4>
                <div className="d-flex align-items-center gap-3">
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={handleReset}
                    disabled={plotLoading}
                  >
                    <i className="ri-refresh-line me-1"></i>
                    Reset All
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Settings Row */}
          <Row className="mb-4">
            {/* Left: General Settings */}
            <Col lg={12}>
              <GeneralSettings
                settings={settings}
                onUpdateSetting={handleSettingUpdate}
                onReset={handleReset}
                config={differentialSilhouetteConfig}
                title="Analysis Settings"
                loading={plotLoading}
                selectedFiles={selectedFiles}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            {/* Right: Seq Types and Groups */}
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={availableGroups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                onSelectAllSeqTypeFiles={handleSelectAllSeqTypeFiles}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                selectedFiles={selectedFiles}
                filteredFiles={filteredFiles}
                allSeqTypeFilesSelected={allSeqTypeFilesSelected}
                allGroupFilesSelected={allGroupFilesSelected}
                showSelectAllCheckboxes={false}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={12}>
              <GroupManager
                groups={groups}
                allFiles={apiData.json_files}
                files={filteredFiles}
                filteredFiles={filteredFiles}
                onAddFilesToGroup={addFilesToGroup}
                onRemoveFilesFromGroup={removeFilesFromGroup}
                onUpdateGroupName={updateGroupName}
                onClearGroup={clearGroup}
                onAutoFill={handleAutoFill}
                onAutoLabel={handleAutoLabel}
                onClearAll={clearAllGroups}
                groupLabels={{
                  group1: groups[0]?.name || "Group 1",
                  group2: groups[1]?.name || "Group 2",
                }}
                onUpdateGroupLabel={(groupId, name) =>
                  updateGroupName(groupId, name)
                }
              />
            </Col>
          </Row>

          {/* Status and Generate Button */}
          <Row className="mb-4">
            <Col lg={12}>
              <div  ref={loadingDivRef}  
                    className="d-flex justify-content-between align-items-center">
                <div className="text-muted">
                  <span>
                    <i className="ri-group-line me-1"></i>
                    {groups[0]?.name || "Group 1"}:{" "}
                    <strong>{groups[0]?.files?.length || 0}</strong> files |{" "}
                    {groups[1]?.name || "Group 2"}:{" "}
                    <strong>{groups[1]?.files?.length || 0}</strong> files{" "}
                    {(groups[0]?.files?.length || 0) === 0 ||
                    (groups[1]?.files?.length || 0) === 0 ? (
                      <span className="text-warning ms-2">
                        Both groups need files for comparison
                      </span>
                    ) : (
                      <i className="ri-check-circle-line ms-2 text-success"></i>
                    )}
                  </span>
                  {taskId && (
                    <span className="ms-2">
                      - <strong>Task ID:</strong> {taskId}
                    </span>
                  )}
                </div>
                <ActionButton
                  onClick={handleGenerate}
                  loading={plotLoading}
                  buttonType="plot"
                />{" "}
              </div>
            </Col>
          </Row>

          {/* Results Container */}
          {(plotData || plotLoading || error) && (
            <Row>
              <Col lg={12}>
                <PlotContainer
                  ref={plotContainerRef}
                  plotData={plotData}
                  loading={plotLoading}
                  error={error}
                  selectedFilesCount={
                    (groups[0]?.files?.length || 0) +
                    (groups[1]?.files?.length || 0)
                  }
                  title="Differential Silhouette Analysis Results"
                  onRetry={handleGenerate}
                  showFileCount={true}
                  isTable={false}
                />
              </Col>
            </Row>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DifferentialSilhouette;
